/**
 * Web Worker プール (画像処理並列化)。
 *
 * - 並列度 = `navigator.hardwareConcurrency` (最大 8)
 * - ジョブキューでアイドルな Worker に順次割り当て
 * - Worker 生成は `WorkerFactory` で差し替え可能 (テストでモック注入)
 */

import type { ImageJobRequest, ImageJobResponse } from '../../web-workers/image-worker';

// ===== Worker 抽象 (DI 用) ===============================================

export interface WorkerLike {
  postMessage(message: ImageJobRequest, transfer?: Transferable[]): void;
  addEventListener(type: 'message', listener: (ev: MessageEvent<ImageJobResponse>) => void): void;
  removeEventListener(
    type: 'message',
    listener: (ev: MessageEvent<ImageJobResponse>) => void,
  ): void;
  terminate(): void;
}

export type WorkerFactory = () => WorkerLike;

const MAX_POOL_SIZE = 8;

/**
 * ブラウザでの標準ファクトリ: Vite bundler が Worker URL を解決する。
 */
export function defaultBrowserWorkerFactory(): WorkerFactory {
  return () =>
    new Worker(new URL('../../web-workers/image-worker.ts', import.meta.url), {
      type: 'module',
    }) as unknown as WorkerLike;
}

export function determinePoolSize(requested?: number): number {
  const hardware =
    typeof navigator !== 'undefined' && navigator.hardwareConcurrency > 0
      ? navigator.hardwareConcurrency
      : 4;
  const size = requested && requested > 0 ? requested : hardware;
  return Math.min(Math.max(1, size), MAX_POOL_SIZE);
}

// ===== キュー型 ==========================================================

interface QueueEntry {
  readonly request: ImageJobRequest;
  readonly resolve: (res: ImageJobResponse) => void;
  readonly reject: (err: Error) => void;
}

// ===== Pool ============================================================

export class ImageWorkerPool {
  private readonly workers: WorkerLike[] = [];
  private readonly idle: WorkerLike[] = [];
  private readonly queue: QueueEntry[] = [];
  private terminated = false;

  constructor(factory: WorkerFactory, size: number = determinePoolSize()) {
    for (let i = 0; i < size; i++) {
      const worker = factory();
      this.workers.push(worker);
      this.idle.push(worker);
    }
  }

  process(request: ImageJobRequest): Promise<ImageJobResponse> {
    if (this.terminated) {
      return Promise.reject(new Error('ImageWorkerPool is terminated'));
    }
    return new Promise<ImageJobResponse>((resolve, reject) => {
      this.queue.push({ request, resolve, reject });
      this.flush();
    });
  }

  private flush(): void {
    while (this.queue.length > 0 && this.idle.length > 0) {
      const worker = this.idle.shift();
      const entry = this.queue.shift();
      if (!worker || !entry) return;
      this.dispatch(worker, entry);
    }
  }

  private dispatch(worker: WorkerLike, entry: QueueEntry): void {
    const handler = (ev: MessageEvent<ImageJobResponse>): void => {
      if (ev.data.id !== entry.request.id) return; // 別ジョブの応答は無視
      worker.removeEventListener('message', handler);
      this.idle.push(worker);
      entry.resolve(ev.data);
      this.flush();
    };
    worker.addEventListener('message', handler);
    worker.postMessage(entry.request, [entry.request.fileBuffer]);
  }

  get size(): number {
    return this.workers.length;
  }

  get pending(): number {
    return this.queue.length;
  }

  terminate(): void {
    this.terminated = true;
    for (const worker of this.workers) {
      worker.terminate();
    }
    while (this.queue.length > 0) {
      const entry = this.queue.shift();
      entry?.reject(new Error('ImageWorkerPool terminated before job dispatched'));
    }
    this.workers.length = 0;
    this.idle.length = 0;
  }
}
